import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  db, doc, updateDoc, deleteDoc, collection, query, where, getDocs,
  serverTimestamp, storage, ref, uploadBytes, getDownloadURL
} from '../../firebase/config';
import {
  ChevronLeft, Camera, Star, Users,
  Calendar, Briefcase, DollarSign, Clock, CreditCard,
  Image as ImageIcon, MapPin, ChevronRight, LogOut, Repeat,
  Bell, ShieldCheck, User, Mail, Phone, X, Check, Pencil,
  Banknote, Wallet, Info, Landmark, Smartphone, Locate, Eye, EyeOff,
  Trash2, Key, Download, AlertTriangle, MessageCircle, Instagram, Globe, Search
} from 'lucide-react';

// ---------- Constants ----------
const DAYS_OF_WEEK = [
  { id: 'mon', label: 'Monday' },
  { id: 'tue', label: 'Tuesday' },
  { id: 'wed', label: 'Wednesday' },
  { id: 'thu', label: 'Thursday' },
  { id: 'fri', label: 'Friday' },
  { id: 'sat', label: 'Saturday' },
  { id: 'sun', label: 'Sunday' },
];

const DEFAULT_WORKING_HOURS = {
  mon: { enabled: true, open: '09:00', close: '17:00' },
  tue: { enabled: true, open: '09:00', close: '17:00' },
  wed: { enabled: true, open: '09:00', close: '17:00' },
  thu: { enabled: true, open: '09:00', close: '17:00' },
  fri: { enabled: true, open: '09:00', close: '17:00' },
  sat: { enabled: true, open: '09:00', close: '14:00' },
  sun: { enabled: false, open: '09:00', close: '17:00' },
};

const SA_BANKS = [
  'ABSA', 'Capitec', 'Discovery Bank', 'FNB', 'Investec',
  'Nedbank', 'Standard Bank', 'TymeBank', 'Other',
];

const DEFAULT_NOTIFICATION_PREFS = {
  bookingUpdates: true,
  messages: true,
  payments: true,
  marketing: false,
  quietHours: false,
};

// ---------- Image compression (shared by cover + avatar) ----------
const compressImage = (file, maxDim = 1024, quality = 0.85) =>
  new Promise((resolve, reject) => {
    if (!file.type.startsWith('image/')) return resolve(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          if (width >= height) {
            height = Math.round((height * maxDim) / width);
            width = maxDim;
          } else {
            width = Math.round((width * maxDim) / height);
            height = maxDim;
          }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => {
            if (!blob) return resolve(file);
            const name = file.name.replace(/\.[^.]+$/, '') + '.jpg';
            resolve(new File([blob], name, { type: 'image/jpeg', lastModified: Date.now() }));
          },
          'image/jpeg',
          quality
        );
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

// ---------- Nominatim Search Helpers ----------
const searchAddress = async (query) => {
  if (!query || query.length < 3) return [];
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=5&addressdetails=1`,
      {
        headers: {
          'User-Agent': 'Skytone/1.0 (contact@skytoneby.co.za)'
        }
      }
    );
    return await response.json();
  } catch (error) {
    console.error('Address search failed:', error);
    return [];
  }
};

const reverseGeocode = async (lat, lng) => {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&addressdetails=1`,
      {
        headers: {
          'User-Agent': 'Skytone/1.0 (contact@skytoneby.co.za)'
        }
      }
    );
    return await response.json();
  } catch (error) {
    console.error('Reverse geocode failed:', error);
    return null;
  }
};

const ProfessionalProfilePage = () => {
  const navigate = useNavigate();
  const { user, userData, setUserData, logout } = useAuth();

  const [coverImage, setCoverImage] = useState(null);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [profileImage, setProfileImage] = useState(null);
  const [uploadingProfile, setUploadingProfile] = useState(false);
  const [stats, setStats] = useState({
    clients: 0, bookings: 0, rating: 0, services: 0, revenue: 0
  });
  const [showEditModal, setShowEditModal] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [switchingMode, setSwitchingMode] = useState(false);
  const [showSwitchModal, setShowSwitchModal] = useState(false);
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [savingPolicy, setSavingPolicy] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [savingPayment, setSavingPayment] = useState(false);
  const [showBankModal, setShowBankModal] = useState(false);
  const [savingBank, setSavingBank] = useState(false);
  const [showHoursModal, setShowHoursModal] = useState(false);
  const [savingHours, setSavingHours] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [savingLocation, setSavingLocation] = useState(false);
  const [showMapVisibilityModal, setShowMapVisibilityModal] = useState(false);
  const [savingMapVisibility, setSavingMapVisibility] = useState(false);
  const [showSocialModal, setShowSocialModal] = useState(false);
  const [savingSocial, setSavingSocial] = useState(false);
  const [showBookingPolicyModal, setShowBookingPolicyModal] = useState(false);
  const [savingBookingPolicy, setSavingBookingPolicy] = useState(false);
  const [locating, setLocating] = useState(false);
  const [toast, setToast] = useState(null);

  // Address Search State
  const [addressQuery, setAddressQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchTimerRef = useRef(null);

  // NEW modals
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showNotificationsModal, setShowNotificationsModal] = useState(false);
  const [savingNotifications, setSavingNotifications] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);

  const coverInputRef = useRef(null);
  const profileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: '', phone: '', email: '', businessName: '', specialization: '', location: '', bio: ''
  });

  // Policy form
  const [policyForm, setPolicyForm] = useState({
    defaultPaymentPolicy: 'after',
    defaultDepositPercent: 30,
  });

  // Payment methods form (PayShap + Cash)
  const [paymentForm, setPaymentForm] = useState({
    acceptPayShap: false,
    payshapId: '',
    payshapName: '',
    payshapNote: '',
    acceptCash: true,
    cashNote: '',
  });

  // Banking form (payouts)
  const [bankForm, setBankForm] = useState({
    bankName: '',
    accountHolder: '',
    accountNumber: '',
    branchCode: '',
    accountType: 'cheque',
  });

  // Working hours
  const [hoursForm, setHoursForm] = useState(DEFAULT_WORKING_HOURS);

  // Location
  const [locationForm, setLocationForm] = useState({
    line1: '',
    city: '',
    postalCode: '',
    country: 'South Africa',
    lat: null,
    lng: null,
  });

  // Map visibility
  const [showOnMap, setShowOnMap] = useState(true);

  // Notification prefs
  const [notificationPrefs, setNotificationPrefs] = useState(DEFAULT_NOTIFICATION_PREFS);

  // Social Links
  const [socialForm, setSocialForm] = useState({
    whatsapp: '',
    instagram: '',
    tiktok: '',
  });

  // Booking Policy
  const [bookingPolicyForm, setBookingPolicyForm] = useState('');

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2500);
  };

  // ---------- Load user data ----------
  useEffect(() => {
    if (userData) {
      setFormData({
        name: userData.name || '',
        phone: userData.phone || '',
        email: userData.email || '',
        businessName: userData.businessName || '',
        specialization: userData.specialization || '',
        location: userData.location || '',
        bio: userData.bio || '',
      });
      setPolicyForm({
        defaultPaymentPolicy: userData.defaultPaymentPolicy || 'after',
        defaultDepositPercent: userData.defaultDepositPercent || 30,
      });
      setPaymentForm({
        acceptPayShap: userData.paymentMethods?.acceptPayShap || false,
        payshapId: userData.paymentMethods?.payshapId || '',
        payshapName: userData.paymentMethods?.payshapName || userData.businessName || userData.name || '',
        payshapNote: userData.paymentMethods?.payshapNote || '',
        acceptCash: userData.paymentMethods?.acceptCash !== false,
        cashNote: userData.paymentMethods?.cashNote || '',
      });
      setBankForm({
        bankName: userData.bankingDetails?.bankName || '',
        accountHolder: userData.bankingDetails?.accountHolder || '',
        accountNumber: userData.bankingDetails?.accountNumber || '',
        branchCode: userData.bankingDetails?.branchCode || '',
        accountType: userData.bankingDetails?.accountType || 'cheque',
      });
      setHoursForm({
        ...DEFAULT_WORKING_HOURS,
        ...(userData.workingHours || {}),
      });
      setLocationForm({
        line1: userData.address?.line1 || '',
        city: userData.address?.city || '',
        postalCode: userData.address?.postalCode || '',
        country: userData.address?.country || 'South Africa',
        lat: userData.address?.lat || null,
        lng: userData.address?.lng || null,
      });
      setAddressQuery(userData.address?.line1 || '');
      
      setShowOnMap(userData.showOnMap !== false);
      setNotificationPrefs({
        ...DEFAULT_NOTIFICATION_PREFS,
        ...(userData.notificationPreferences || {}),
      });
      setSocialForm({
        whatsapp: userData.socialLinks?.whatsapp || '',
        instagram: userData.socialLinks?.instagram || '',
        tiktok: userData.socialLinks?.tiktok || '',
      });
      setBookingPolicyForm(userData.bookingPolicy || '');
      
      if (userData.coverImage) setCoverImage(userData.coverImage);
      if (userData.profileImage) setProfileImage(userData.profileImage);
    }
  }, [userData]);

  // ---------- Load stats ----------
  useEffect(() => {
    if (!user?.uid) return;

    const loadStats = async () => {
      try {
        const bookingsSnapshot = await getDocs(
          query(collection(db, 'bookings'), where('professionalId', '==', user.uid))
        );
        const bookingsData = bookingsSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));

        const servicesSnapshot = await getDocs(
          query(collection(db, 'services'), where('professionalId', '==', user.uid))
        );
        const servicesData = servicesSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));

        const uniqueClients = new Set(bookingsData.map(b => b.clientId).filter(Boolean)).size;
        const totalRevenue = bookingsData
          .filter(b => b.status === 'completed' || b.status === 'confirmed')
          .reduce((sum, b) => sum + (b.price || 0), 0);
        const ratedBookings = bookingsData.filter(b => typeof b.rating === 'number' && b.rating > 0);
        const avgRating = ratedBookings.length
          ? ratedBookings.reduce((sum, b) => sum + b.rating, 0) / ratedBookings.length
          : 0;

        setStats({
          clients: uniqueClients,
          bookings: bookingsData.length,
          rating: avgRating,
          services: servicesData.length,
          revenue: totalRevenue,
        });
      } catch (err) {
        console.error('Error loading stats:', err);
      }
    };

    loadStats();
  }, [user?.uid]);

  // ---------- Cover upload ----------
  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      showToast('Please choose an image file', 'error');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showToast('Image must be under 5MB', 'error');
      return;
    }

    // instant preview
    const previewUrl = URL.createObjectURL(file);
    const prevCover = coverImage;
    setCoverImage(previewUrl);
    setUploadingCover(true);

    try {
      const compressed = await compressImage(file, 1600, 0.85);
      const storageRef = ref(storage, `covers/${user.uid}/${Date.now()}_${compressed.name}`);
      await uploadBytes(storageRef, compressed, { contentType: 'image/jpeg' });
      const url = await getDownloadURL(storageRef);

      await updateDoc(doc(db, 'users', user.uid), {
        coverImage: url,
        updatedAt: serverTimestamp(),
      });

      URL.revokeObjectURL(previewUrl);
      setCoverImage(url);
      if (setUserData) setUserData(prev => ({ ...prev, coverImage: url }));
      showToast('Cover updated');
    } catch (err) {
      console.error('Cover upload error:', err);
      URL.revokeObjectURL(previewUrl);
      setCoverImage(prevCover);
      showToast('Failed to upload cover', 'error');
    } finally {
      setUploadingCover(false);
      if (coverInputRef.current) coverInputRef.current.value = '';
    }
  };

  // ---------- Profile (avatar) upload ----------
  const handleProfileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      showToast('Please choose an image file', 'error');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showToast('Image must be under 5MB', 'error');
      return;
    }

    const previewUrl = URL.createObjectURL(file);
    const prevImage = profileImage;
    setProfileImage(previewUrl);
    setUploadingProfile(true);

    try {
      const compressed = await compressImage(file, 1024, 0.85);
      const storageRef = ref(storage, `avatars/${user.uid}/${Date.now()}_${compressed.name}`);
      await uploadBytes(storageRef, compressed, { contentType: 'image/jpeg' });
      const url = await getDownloadURL(storageRef);

      await updateDoc(doc(db, 'users', user.uid), {
        profileImage: url,
        updatedAt: serverTimestamp(),
      });

      URL.revokeObjectURL(previewUrl);
      setProfileImage(url);
      if (setUserData) setUserData(prev => ({ ...prev, profileImage: url }));
      showToast('Profile photo updated');
    } catch (err) {
      console.error('Profile upload error:', err);
      URL.revokeObjectURL(previewUrl);
      setProfileImage(prevImage);
      showToast('Failed to upload profile photo', 'error');
    } finally {
      setUploadingProfile(false);
      if (profileInputRef.current) profileInputRef.current.value = '';
    }
  };

  const handleRemoveProfileImage = async () => {
    if (!window.confirm('Remove your profile photo?')) return;
    setUploadingProfile(true);
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        profileImage: null,
        updatedAt: serverTimestamp(),
      });
      setProfileImage(null);
      if (setUserData) setUserData(prev => ({ ...prev, profileImage: null }));
      showToast('Profile photo removed');
    } catch (err) {
      console.error('Remove profile photo error:', err);
      showToast('Failed to remove profile photo', 'error');
    } finally {
      setUploadingProfile(false);
    }
  };

  // ---------- Save profile ----------
  const handleSave = async () => {
    if (!formData.businessName.trim()) {
      showToast('Business name is required', 'error');
      return;
    }
    setSavingProfile(true);
    try {
      const updates = {
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        businessName: formData.businessName.trim(),
        businessNameLower: formData.businessName.trim().toLowerCase(),
        specialization: formData.specialization.trim(),
        location: formData.location.trim(),
        bio: formData.bio.trim(),
        updatedAt: serverTimestamp(),
      };
      await updateDoc(doc(db, 'users', user.uid), updates);
      if (setUserData) setUserData(prev => ({ ...prev, ...updates }));
      setShowEditModal(false);
      showToast('Profile updated');
    } catch (err) {
      console.error('Save profile error:', err);
      showToast('Failed to save profile', 'error');
    } finally {
      setSavingProfile(false);
    }
  };

  // ---------- Save Social Links ----------
  const handleSaveSocial = async () => {
    setSavingSocial(true);
    try {
      const socialLinks = {
        whatsapp: socialForm.whatsapp.trim(),
        instagram: socialForm.instagram.trim(),
        tiktok: socialForm.tiktok.trim(),
      };
      await updateDoc(doc(db, 'users', user.uid), {
        socialLinks,
        updatedAt: serverTimestamp(),
      });
      if (setUserData) setUserData(prev => ({ ...prev, socialLinks }));
      setShowSocialModal(false);
      showToast('Social links updated');
    } catch (err) {
      console.error('Save social error:', err);
      showToast('Failed to save social links', 'error');
    } finally {
      setSavingSocial(false);
    }
  };

  // ---------- Save Booking Policy ----------
  const handleSaveBookingPolicy = async () => {
    setSavingBookingPolicy(true);
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        bookingPolicy: bookingPolicyForm.trim(),
        updatedAt: serverTimestamp(),
      });
      if (setUserData) setUserData(prev => ({ ...prev, bookingPolicy: bookingPolicyForm.trim() }));
      setShowBookingPolicyModal(false);
      showToast('Booking policy updated');
    } catch (err) {
      console.error('Save booking policy error:', err);
      showToast('Failed to save booking policy', 'error');
    } finally {
      setSavingBookingPolicy(false);
    }
  };

  // ---------- Save payment policy ----------
  const handleSavePolicy = async () => {
    setSavingPolicy(true);
    try {
      const updates = {
        defaultPaymentPolicy: policyForm.defaultPaymentPolicy,
        defaultDepositPercent: policyForm.defaultDepositPercent,
        updatedAt: serverTimestamp(),
      };
      await updateDoc(doc(db, 'users', user.uid), updates);
      if (setUserData) setUserData(prev => ({ ...prev, ...updates }));
      setShowPolicyModal(false);
      showToast('Payment policy updated');
    } catch (err) {
      console.error('Save policy error:', err);
      showToast('Failed to save policy', 'error');
    } finally {
      setSavingPolicy(false);
    }
  };

  // ---------- Save payment methods ----------
  const handleSavePayment = async () => {
    if (paymentForm.acceptPayShap && !paymentForm.payshapId.trim()) {
      showToast('Enter a PayShap ID or turn PayShap off', 'error');
      return;
    }

    setSavingPayment(true);
    try {
      const paymentMethods = {
        acceptPayShap: paymentForm.acceptPayShap,
        payshapId: paymentForm.payshapId.trim(),
        payshapName: paymentForm.payshapName.trim(),
        payshapNote: paymentForm.payshapNote.trim(),
        acceptCash: paymentForm.acceptCash,
        cashNote: paymentForm.cashNote.trim(),
        updatedAt: new Date().toISOString(),
      };
      await updateDoc(doc(db, 'users', user.uid), {
        paymentMethods,
        updatedAt: serverTimestamp(),
      });
      if (setUserData) setUserData(prev => ({ ...prev, paymentMethods }));
      setShowPaymentModal(false);
      showToast('Payment methods updated');
    } catch (err) {
      console.error('Save payment methods error:', err);
      showToast('Failed to save payment methods', 'error');
    } finally {
      setSavingPayment(false);
    }
  };

  // ---------- Save banking details ----------
  const handleSaveBank = async () => {
    if (
      !bankForm.bankName.trim() ||
      !bankForm.accountHolder.trim() ||
      !bankForm.accountNumber.trim()
    ) {
      showToast('Fill in bank, account holder, and account number', 'error');
      return;
    }

    setSavingBank(true);
    try {
      const bankingDetails = {
        bankName: bankForm.bankName.trim(),
        accountHolder: bankForm.accountHolder.trim(),
        accountNumber: bankForm.accountNumber.trim(),
        branchCode: bankForm.branchCode.trim(),
        accountType: bankForm.accountType,
        updatedAt: new Date().toISOString(),
      };
      await updateDoc(doc(db, 'users', user.uid), {
        bankingDetails,
        updatedAt: serverTimestamp(),
      });
      if (setUserData) setUserData(prev => ({ ...prev, bankingDetails }));
      setShowBankModal(false);
      showToast('Banking details saved');
    } catch (err) {
      console.error('Save banking error:', err);
      showToast('Failed to save banking details', 'error');
    } finally {
      setSavingBank(false);
    }
  };

  // ---------- Save working hours ----------
  const handleSaveHours = async () => {
    setSavingHours(true);
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        workingHours: hoursForm,
        updatedAt: serverTimestamp(),
      });
      if (setUserData) setUserData(prev => ({ ...prev, workingHours: hoursForm }));
      setShowHoursModal(false);
      showToast('Working hours updated');
    } catch (err) {
      console.error('Save hours error:', err);
      showToast('Failed to save working hours', 'error');
    } finally {
      setSavingHours(false);
    }
  };

  // ---------- Save location ----------
  const handleSaveLocation = async () => {
    if (!locationForm.line1.trim() || !locationForm.city.trim()) {
      showToast('Address and city are required', 'error');
      return;
    }

    setSavingLocation(true);
    try {
      const address = {
        line1: locationForm.line1.trim(),
        city: locationForm.city.trim(),
        postalCode: locationForm.postalCode.trim(),
        country: locationForm.country.trim() || 'South Africa',
        lat: locationForm.lat,
        lng: locationForm.lng,
        updatedAt: new Date().toISOString(),
      };
      const locationString = `${address.city}, ${address.country}`;

      const coordinates = (address.lat != null && address.lng != null)
        ? { lat: address.lat, lng: address.lng, label: address.city }
        : null;

      await updateDoc(doc(db, 'users', user.uid), {
        address,
        location: locationString,
        ...(coordinates ? { coordinates } : {}),
        updatedAt: serverTimestamp(),
      });

      if (setUserData) {
        setUserData(prev => ({
          ...prev,
          address,
          location: locationString,
          ...(coordinates ? { coordinates } : {}),
        }));
      }

      setShowLocationModal(false);
      showToast('Location saved');
    } catch (err) {
      console.error('Save location error:', err);
      showToast('Failed to save location', 'error');
    } finally {
      setSavingLocation(false);
    }
  };

  // ---------- Save map visibility ----------
  const handleSaveMapVisibility = async () => {
    setSavingMapVisibility(true);
    try {
      const next = showOnMap;
      await updateDoc(doc(db, 'users', user.uid), {
        showOnMap: next,
        updatedAt: serverTimestamp(),
      });
      if (setUserData) setUserData(prev => ({ ...prev, showOnMap: next }));
      setShowMapVisibilityModal(false);
      showToast(next ? 'You are now visible on the map' : 'You are now hidden from the map');
    } catch (err) {
      console.error('Save map visibility error:', err);
      showToast('Failed to save map visibility', 'error');
    } finally {
      setSavingMapVisibility(false);
    }
  };

  // ---------- Save notification preferences ----------
  const handleSaveNotifications = async () => {
    setSavingNotifications(true);
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        notificationPreferences: notificationPrefs,
        updatedAt: serverTimestamp(),
      });
      if (setUserData) setUserData(prev => ({ ...prev, notificationPreferences: notificationPrefs }));
      setShowNotificationsModal(false);
      showToast('Notification preferences saved');
    } catch (err) {
      console.error('Save notifications error:', err);
      showToast('Failed to save preferences', 'error');
    } finally {
      setSavingNotifications(false);
    }
  };

  // ---------- Delete account ----------
  const handleDeleteAccount = async () => {
    if (deleteConfirmText.trim().toUpperCase() !== 'DELETE') {
      showToast('Type DELETE to confirm', 'error');
      return;
    }

    setDeletingAccount(true);
    try {
      const servicesSnap = await getDocs(
        query(collection(db, 'services'), where('professionalId', '==', user.uid))
      );
      await Promise.all(servicesSnap.docs.map(d => deleteDoc(doc(db, 'services', d.id))));

      const productsSnap = await getDocs(
        query(collection(db, 'products'), where('professionalId', '==', user.uid))
      );
      await Promise.all(productsSnap.docs.map(d => deleteDoc(doc(db, 'products', d.id))));

      await deleteDoc(doc(db, 'users', user.uid));

      await logout();
      navigate('/login');
    } catch (err) {
      console.error('Delete account error:', err);
      showToast('Failed to delete account. Please try again.', 'error');
      setDeletingAccount(false);
    }
  };

  // ---------- Geo locate ----------
  const handleGeolocate = () => {
    if (!navigator.geolocation) {
      showToast('Geolocation not supported', 'error');
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        
        setLocationForm(prev => ({ ...prev, lat, lng }));
        showToast('Location captured, fetching address...', 'success');
        
        const data = await reverseGeocode(lat, lng);
        if (data && data.address) {
          const street = data.address.road || data.address.pedestrian || data.address.suburb || '';
          const city = data.address.city || data.address.town || data.address.village || '';
          const postalCode = data.address.postcode || '';
          const country = data.address.country || 'South Africa';

          setLocationForm(prev => ({
            ...prev,
            line1: street,
            city,
            postalCode,
            country,
          }));
          setAddressQuery(data.display_name);
        }
        setLocating(false);
      },
      (err) => {
        console.error('Geolocation error:', err);
        showToast('Could not get location', 'error');
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  };

  // ---------- Switch to client ----------
  const confirmSwitchToClient = async () => {
    setSwitchingMode(true);
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        activeMode: 'client',
        updatedAt: serverTimestamp(),
      });
      await logout();
      navigate('/login');
    } catch (err) {
      console.error('Switch error:', err);
      showToast('Failed to switch mode', 'error');
      setSwitchingMode(false);
    }
  };

  // ---------- Logout ----------
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  // ---------- Helpers ----------
  const getPolicyLabel = (policy, depositPct) => {
    if (policy === 'full') return 'Full payment upfront';
    if (policy === 'deposit') return `${depositPct}% deposit`;
    return 'Pay after service';
  };

  const getPolicyIcon = (policy) => {
    if (policy === 'full') return <Wallet size={18} />;
    if (policy === 'deposit') return <CreditCard size={18} />;
    return <Banknote size={18} />;
  };

  const getPaymentSubtitle = () => {
    const parts = [];
    if (paymentForm.acceptPayShap && paymentForm.payshapId) parts.push('PayShap');
    if (paymentForm.acceptCash) parts.push('Cash');
    if (parts.length === 0) return 'No methods set up';
    return parts.join(' · ');
  };

  const getBankSubtitle = () => {
    if (!bankForm.bankName) return 'Not set — tap to add';
    const last4 = bankForm.accountNumber.slice(-4);
    return `${bankForm.bankName} · ****${last4}`;
  };

  const getHoursSubtitle = () => {
    const enabledDays = Object.entries(hoursForm).filter(([_, d]) => d.enabled);
    if (enabledDays.length === 0) return 'No days set';
    const first = enabledDays[0][1];
    return `${enabledDays.length} days/week · ${first.open}–${first.close}`;
  };

  const getLocationSubtitle = () => {
    if (!locationForm.line1) return 'Address not set';
    return `${locationForm.line1}, ${locationForm.city}`;
  };

  const getMapVisibilitySubtitle = () => {
    if (!userData?.coordinates?.lat) return 'Set your location first';
    return showOnMap ? 'Visible to clients on the map' : 'Hidden from the map';
  };

  const getSocialSubtitle = () => {
    const parts = [];
    if (socialForm.whatsapp) parts.push('WhatsApp');
    if (socialForm.instagram) parts.push('Instagram');
    if (socialForm.tiktok) parts.push('TikTok');
    if (parts.length === 0) return 'Add your social links';
    return parts.join(' · ');
  };

  // ---------- Settings rows ----------
  const businessRows = [
    {
      icon: getPolicyIcon(policyForm.defaultPaymentPolicy),
      title: 'Payment Policy',
      subtitle: `Default for new services — ${getPolicyLabel(policyForm.defaultPaymentPolicy, policyForm.defaultDepositPercent)}`,
      onClick: () => setShowPolicyModal(true),
    },
    {
      icon: <Smartphone size={18} />,
      title: 'Payment Methods',
      subtitle: getPaymentSubtitle(),
      onClick: () => setShowPaymentModal(true),
    },
    {
      icon: <Landmark size={18} />,
      title: 'Banking Details',
      subtitle: getBankSubtitle(),
      onClick: () => setShowBankModal(true),
    },
    {
      icon: <Clock size={18} />,
      title: 'Working Hours',
      subtitle: getHoursSubtitle(),
      onClick: () => setShowHoursModal(true),
    },
    {
      icon: <MapPin size={18} />,
      title: 'Location',
      subtitle: getLocationSubtitle(),
      onClick: () => setShowLocationModal(true),
    },
    {
      icon: showOnMap ? <Eye size={18} /> : <EyeOff size={18} />,
      title: 'Show me on the map',
      subtitle: getMapVisibilitySubtitle(),
      onClick: () => setShowMapVisibilityModal(true),
    },
    {
      icon: <Globe size={18} />,
      title: 'Social Links',
      subtitle: getSocialSubtitle(),
      onClick: () => setShowSocialModal(true),
    },
    {
      icon: <Info size={18} />,
      title: 'Booking Policy',
      subtitle: bookingPolicyForm ? 'Set' : 'Add a policy for clients',
      onClick: () => setShowBookingPolicyModal(true),
    },
    {
      icon: <ImageIcon size={18} />,
      title: 'Portfolio',
      subtitle: 'Manage your services & work',
      onClick: () => navigate('/professional-dashboard/services'),
    },
  ];

  const accountRows = [
    { icon: <ShieldCheck size={18} />, title: 'Privacy & Security', onClick: () => setShowPrivacyModal(true) },
    { icon: <Bell size={18} />, title: 'Notifications', onClick: () => setShowNotificationsModal(true) },
    { icon: <Trash2 size={18} />, title: 'Delete Account', subtitle: 'Permanently delete your account', onClick: () => setShowDeleteModal(true), danger: true },
  ];

  const statsItems = [
    { label: 'Rating', value: stats.rating > 0 ? stats.rating.toFixed(1) : '0', icon: <Star size={18} /> },
    { label: 'Clients', value: stats.clients, icon: <Users size={18} /> },
    { label: 'Bookings', value: stats.bookings, icon: <Calendar size={18} /> },
    { label: 'Services', value: stats.services, icon: <Briefcase size={18} /> },
    { label: 'Revenue', value: `R${stats.revenue.toLocaleString()}`, icon: <DollarSign size={18} /> },
  ];

  // ============================================================
  // DESKTOP VIEW
  // ============================================================
  const DesktopView = () => (
    <div className="hidden lg:block max-w-4xl mx-auto w-full py-10">
      <div className="mb-8">
        <h1 className="text-3xl font-serif font-bold text-gray-900">Professional Profile</h1>
        <p className="text-gray-500 mt-1">Manage your business profile</p>
      </div>

      {/* Cover */}
      <div className="relative w-full h-64 rounded-3xl overflow-hidden bg-[#F4EEFF] mb-8 border border-gray-100">
        {coverImage ? (
          <img src={coverImage} alt="Cover" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ImageIcon size={48} className="text-[#CDBCE7]" />
          </div>
        )}
        {uploadingCover && (
          <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
            <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}
        <label className={`absolute bottom-4 right-4 w-11 h-11 rounded-full flex items-center justify-center z-20 cursor-pointer transition-colors ${
          uploadingCover ? 'bg-white/70 cursor-wait' : 'bg-white hover:bg-[#F4EEFF]'
        }`}>
          {uploadingCover ? (
            <div className="w-4 h-4 border-2 border-[#AF8BD4] border-t-transparent rounded-full animate-spin"></div>
          ) : (
            <Camera size={18} className="text-gray-700" />
          )}
          <input
            ref={coverInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleCoverUpload}
            disabled={uploadingCover}
          />
        </label>
      </div>

      <div className="flex items-start gap-6 mb-8">
        {/* Avatar with camera overlay */}
        <div className="relative shrink-0">
          <div className="w-24 h-24 rounded-full bg-[#F4EEFF] flex items-center justify-center text-[#AF8BD4] overflow-hidden">
            {profileImage ? (
              <img src={profileImage} alt="Profile" className="w-full h-full object-cover" />
            ) : (
              <User size={40} />
            )}
            {uploadingProfile && (
              <div className="absolute inset-0 bg-black/40 rounded-full flex items-center justify-center">
                <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}
          </div>

          {/* Camera button */}
          <label
            className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-[#010101] text-white flex items-center justify-center shadow-md cursor-pointer hover:bg-black/80 transition-colors"
            title="Change profile photo"
          >
            <Camera size={14} />
            <input
              ref={profileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleProfileUpload}
              disabled={uploadingProfile}
            />
          </label>

          {/* Remove photo (only if there is one) */}
          {profileImage && !uploadingProfile && (
            <button
              type="button"
              onClick={handleRemoveProfileImage}
              className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center shadow-md hover:bg-red-600 transition-colors"
              title="Remove photo"
            >
              <X size={12} />
            </button>
          )}
        </div>

        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="text-2xl font-serif font-bold text-gray-900">
              {userData?.businessName || userData?.name || 'Your Business'}
            </h2>
            <div className="bg-[#F4EEFF] text-[#AF8BD4] px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest">
              Pro
            </div>
            <button
              onClick={() => setShowEditModal(true)}
              className="w-8 h-8 rounded-full bg-[#FCF8F9] hover:bg-[#F4EEFF] hover:text-[#AF8BD4] text-gray-500 flex items-center justify-center transition-colors"
              title="Edit profile"
            >
              <Pencil size={13} />
            </button>
          </div>
          <p className="text-sm text-gray-500 mt-1">{userData?.specialization || 'Beauty Professional'}</p>
          <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-400">
            <span className="flex items-center gap-1"><MapPin size={14} /> {userData?.location || 'Location not set'}</span>
            <span className="flex items-center gap-1"><Mail size={14} /> {userData?.email || 'No email'}</span>
            <span className="flex items-center gap-1"><Phone size={14} /> {userData?.phone || 'Not set'}</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-5 gap-3 mb-8">
        {statsItems.map((s, i) => (
          <div key={i} className="bg-white rounded-2xl p-4 border border-gray-100">
            <div className="w-8 h-8 bg-[#F4EEFF] rounded-full flex items-center justify-center text-[#AF8BD4] mb-2">
              {s.icon}
            </div>
            <p className="text-lg font-bold text-gray-900 truncate">{s.value}</p>
            <p className="text-[10px] text-gray-400 uppercase tracking-wider">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="space-y-6">
        <h3 className="text-lg font-serif font-bold text-gray-900">Business Operations</h3>
        <div className="bg-white rounded-3xl border border-gray-100 overflow-hidden">
          {businessRows.map((item, idx) => (
            <div key={idx}>
              <button
                onClick={item.onClick}
                className="w-full p-4 flex items-center gap-4 hover:bg-[#FCF8F9] transition-colors group text-left"
              >
                <div className="w-10 h-10 rounded-full bg-[#FCF8F9] text-gray-500 flex items-center justify-center group-hover:text-[#AF8BD4] group-hover:bg-[#F4EEFF] transition-colors">
                  {item.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm text-gray-900">{item.title}</div>
                  <div className="text-[11px] text-gray-400 font-medium mt-0.5 truncate">{item.subtitle}</div>
                </div>
                <ChevronRight size={16} className="text-gray-300 group-hover:text-[#AF8BD4] transition-colors shrink-0" />
              </button>
              {idx !== businessRows.length - 1 && <div className="h-px bg-gray-50 ml-16"></div>}
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 space-y-6">
        <h3 className="text-lg font-serif font-bold text-gray-900">Account</h3>
        <div className="bg-white rounded-3xl border border-gray-100 overflow-hidden">
          {accountRows.map((item, idx) => (
            <div key={idx}>
              <button
                onClick={item.onClick}
                className={`w-full p-4 flex items-center gap-4 transition-colors group text-left ${
                  item.danger ? 'hover:bg-red-50' : 'hover:bg-[#FCF8F9]'
                }`}
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                  item.danger
                    ? 'bg-red-50 text-red-400 group-hover:bg-red-100'
                    : 'bg-[#FCF8F9] text-gray-500 group-hover:text-[#AF8BD4] group-hover:bg-[#F4EEFF]'
                }`}>
                  {item.icon}
                </div>
                <div className="flex-1">
                  <div className={`font-bold text-sm ${item.danger ? 'text-red-500' : 'text-gray-900'}`}>
                    {item.title}
                  </div>
                  {item.subtitle && (
                    <div className="text-[11px] text-gray-400 font-medium mt-0.5">{item.subtitle}</div>
                  )}
                </div>
                <ChevronRight size={16} className={`transition-colors ${
                  item.danger ? 'text-red-300 group-hover:text-red-500' : 'text-gray-300 group-hover:text-[#AF8BD4]'
                }`} />
              </button>
              <div className="h-px bg-gray-50 ml-16"></div>
            </div>
          ))}

          <button
            onClick={handleLogout}
            className="w-full p-4 flex items-center gap-4 hover:bg-red-50 transition-colors group text-left"
          >
            <div className="w-10 h-10 rounded-full bg-red-50 text-red-400 flex items-center justify-center group-hover:bg-red-100 transition-colors">
              <LogOut size={18} />
            </div>
            <div className="flex-1">
              <div className="font-bold text-sm text-red-500">Log Out</div>
            </div>
          </button>
          <div className="h-px bg-gray-50 ml-16"></div>

          <button
            onClick={() => setShowSwitchModal(true)}
            disabled={switchingMode}
            className="w-full p-4 flex items-center gap-4 hover:bg-[#F4EEFF] transition-colors group text-left disabled:opacity-60"
          >
            <div className="w-10 h-10 rounded-full bg-[#F4EEFF] text-[#AF8BD4] flex items-center justify-center group-hover:bg-[#AF8BD4] group-hover:text-white transition-colors">
              <Repeat size={18} />
            </div>
            <div className="flex-1">
              <div className="font-bold text-sm text-[#AF8BD4]">Switch to Client Mode</div>
              <div className="text-[11px] text-gray-400 font-medium mt-0.5">You'll be signed out</div>
            </div>
            <ChevronRight size={16} className="text-gray-300 group-hover:text-[#AF8BD4] transition-colors" />
          </button>
        </div>
      </div>
    </div>
  );

  // ============================================================
  // MOBILE VIEW
  // ============================================================
  const MobileView = () => (
    <div className="lg:hidden relative w-full max-w-md mx-auto bg-[#FCF8F9] font-sans text-gray-900">
      <div className="pb-4">
        <div className="relative w-full aspect-square shrink-0 bg-[#F4EEFF]">
          {coverImage ? (
            <img src={coverImage} alt="Cover" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <ImageIcon size={48} className="text-[#CDBCE7]" />
            </div>
          )}

          {uploadingCover && (
            <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
              <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}

          <div className="absolute top-12 left-6 right-6 flex justify-between z-20">
            <button
              onClick={() => navigate('/professional-dashboard')}
              className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-gray-800 shadow-sm hover:bg-gray-50 transition-colors"
            >
              <ChevronLeft size={20} />
            </button>
          </div>

          <label className={`absolute bottom-6 right-6 w-11 h-11 rounded-full flex items-center justify-center z-20 cursor-pointer transition-colors shadow-sm ${
            uploadingCover ? 'bg-white/70 cursor-wait' : 'bg-white hover:bg-[#F4EEFF]'
          }`}>
            {uploadingCover ? (
              <div className="w-4 h-4 border-2 border-[#AF8BD4] border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <Camera size={18} className="text-gray-700" />
            )}
            <input
              ref={coverInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleCoverUpload}
              disabled={uploadingCover}
            />
          </label>
        </div>

        <div className="bg-[#FCF8F9] -mt-6 rounded-t-[2rem] relative z-10">
          <div className="px-6 pt-8 mb-6">
            <div className="flex justify-between items-start mb-2 gap-3">
              <div className="min-w-0 flex-1 flex items-center gap-3">
                {/* Mobile avatar with camera */}
                <div className="relative shrink-0">
                  <div className="w-16 h-16 rounded-full bg-[#F4EEFF] flex items-center justify-center text-[#AF8BD4] overflow-hidden border-2 border-white shadow-sm">
                    {profileImage ? (
                      <img src={profileImage} alt="Profile" className="w-full h-full object-cover" />
                    ) : (
                      <User size={28} />
                    )}
                    {uploadingProfile && (
                      <div className="absolute inset-0 bg-black/40 rounded-full flex items-center justify-center">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    )}
                  </div>
                  <label
                    className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-[#010101] text-white flex items-center justify-center shadow-md cursor-pointer"
                    title="Change photo"
                  >
                    <Camera size={11} />
                    <input
                      ref={profileInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleProfileUpload}
                      disabled={uploadingProfile}
                    />
                  </label>
                </div>

                <div className="min-w-0 flex-1">
                  <h1 className="text-xl font-serif font-bold text-gray-900 leading-tight mb-1 truncate">
                    {userData?.businessName || userData?.name || 'Your Business'}
                  </h1>
                  <p className="text-xs text-gray-500 font-medium truncate">
                    {userData?.specialization || 'Beauty Professional'}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => setShowEditModal(true)}
                  className="w-8 h-8 rounded-full bg-[#F4EEFF] text-[#AF8BD4] flex items-center justify-center hover:bg-[#CDBCE7] hover:text-white transition-colors"
                  title="Edit profile"
                >
                  <Pencil size={13} />
                </button>
              </div>
            </div>
            <div className="flex items-center gap-1.5 text-[12px] text-gray-400 font-medium mt-2">
              <MapPin size={14} /> {userData?.location || 'Location not set'}
            </div>
          </div>

          <div className="px-6 mb-8">
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-white p-4 rounded-3xl border border-gray-100 flex flex-col items-center text-center">
                <Star size={18} className="text-[#E8B48E] mb-2" />
                <div className="font-bold text-gray-900 text-lg leading-none mb-1">
                  {stats.rating > 0 ? stats.rating.toFixed(1) : '0'}
                </div>
                <div className="text-[10px] text-gray-400 font-medium uppercase tracking-wider">Rating</div>
              </div>
              <div className="bg-white p-4 rounded-3xl border border-gray-100 flex flex-col items-center text-center">
                <Users size={18} className="text-[#AF8BD4] mb-2" />
                <div className="font-bold text-gray-900 text-lg leading-none mb-1">{stats.clients}</div>
                <div className="text-[10px] text-gray-400 font-medium uppercase tracking-wider">Clients</div>
              </div>
              <div className="bg-white p-4 rounded-3xl border border-gray-100 flex flex-col items-center text-center">
                <Calendar size={18} className="text-[#8BB48E] mb-2" />
                <div className="font-bold text-gray-900 text-lg leading-none mb-1">{stats.bookings}</div>
                <div className="text-[10px] text-gray-400 font-medium uppercase tracking-wider">Bookings</div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div className="bg-white p-4 rounded-3xl border border-gray-100 flex items-center gap-3">
                <Briefcase size={18} className="text-[#AF8BD4]" />
                <div>
                  <div className="font-bold text-gray-900 text-lg leading-none">{stats.services}</div>
                  <div className="text-[10px] text-gray-400 font-medium uppercase tracking-wider mt-0.5">Services</div>
                </div>
              </div>
              <div className="bg-white p-4 rounded-3xl border border-gray-100 flex items-center gap-3">
                <DollarSign size={18} className="text-[#8BB48E]" />
                <div>
                  <div className="font-bold text-gray-900 text-lg leading-none">R{stats.revenue.toLocaleString()}</div>
                  <div className="text-[10px] text-gray-400 font-medium uppercase tracking-wider mt-0.5">Revenue</div>
                </div>
              </div>
            </div>
          </div>

          <div className="px-6 space-y-8">
            <div>
              <h2 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-3 ml-2">Business Operations</h2>
              <div className="bg-white rounded-3xl border border-gray-100 overflow-hidden">
                {businessRows.map((item, idx) => (
                  <div key={idx}>
                    <button
                      onClick={item.onClick}
                      className="w-full p-4 flex items-center gap-4 hover:bg-[#FCF8F9] transition-colors group text-left"
                    >
                      <div className="w-10 h-10 rounded-full bg-[#FCF8F9] text-gray-500 flex items-center justify-center group-hover:text-[#AF8BD4] group-hover:bg-[#F4EEFF] transition-colors shrink-0">
                        {item.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-sm text-gray-900">{item.title}</div>
                        <div className="text-[11px] text-gray-400 font-medium mt-0.5 truncate">{item.subtitle}</div>
                      </div>
                      <ChevronRight size={16} className="text-gray-300 group-hover:text-[#AF8BD4] transition-colors shrink-0" />
                    </button>
                    {idx !== businessRows.length - 1 && <div className="h-px bg-gray-50 ml-16"></div>}
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h2 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-3 ml-2">Account</h2>
              <div className="bg-white rounded-3xl border border-gray-100 overflow-hidden">
                {accountRows.map((item, idx) => (
                  <div key={idx}>
                    <button
                      onClick={item.onClick}
                      className={`w-full p-4 flex items-center gap-4 transition-colors group text-left ${
                        item.danger ? 'hover:bg-red-50' : 'hover:bg-[#FCF8F9]'
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                        item.danger
                          ? 'bg-red-50 text-red-400 group-hover:bg-red-100'
                          : 'bg-[#FCF8F9] text-gray-500 group-hover:text-[#AF8BD4] group-hover:bg-[#F4EEFF]'
                      }`}>
                        {item.icon}
                      </div>
                      <div className="flex-1">
                        <div className={`font-bold text-sm ${item.danger ? 'text-red-500' : 'text-gray-900'}`}>
                          {item.title}
                        </div>
                        {item.subtitle && (
                          <div className="text-[11px] text-gray-400 font-medium mt-0.5">{item.subtitle}</div>
                        )}
                      </div>
                      <ChevronRight size={16} className={`transition-colors ${
                        item.danger ? 'text-red-300 group-hover:text-red-500' : 'text-gray-300 group-hover:text-[#AF8BD4]'
                      }`} />
                    </button>
                    <div className="h-px bg-gray-50 ml-16"></div>
                  </div>
                ))}

                <button
                  onClick={handleLogout}
                  className="w-full p-4 flex items-center gap-4 hover:bg-red-50 transition-colors group text-left"
                >
                  <div className="w-10 h-10 rounded-full bg-red-50 text-red-400 flex items-center justify-center group-hover:bg-red-100 transition-colors">
                    <LogOut size={18} />
                  </div>
                  <div className="flex-1">
                    <div className="font-bold text-sm text-red-500">Log Out</div>
                  </div>
                </button>
                <div className="h-px bg-gray-50 ml-16"></div>

                <button
                  onClick={() => setShowSwitchModal(true)}
                  disabled={switchingMode}
                  className="w-full p-4 flex items-center gap-4 hover:bg-[#F4EEFF] transition-colors group text-left disabled:opacity-60"
                >
                  <div className="w-10 h-10 rounded-full bg-[#F4EEFF] text-[#AF8BD4] flex items-center justify-center group-hover:bg-[#AF8BD4] group-hover:text-white transition-colors">
                    <Repeat size={18} />
                  </div>
                  <div className="flex-1">
                    <div className="font-bold text-sm text-[#AF8BD4]">Switch to Client Mode</div>
                    <div className="text-[11px] text-gray-400 font-medium mt-0.5">You'll be signed out</div>
                  </div>
                  <ChevronRight size={16} className="text-gray-300 group-hover:text-[#AF8BD4] transition-colors" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // ============================================================
  // EDIT PROFILE MODAL
  // ============================================================
  const EditModal = () => (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50">
      <div className="bg-white rounded-t-3xl sm:rounded-3xl max-w-lg w-full max-h-[92vh] overflow-hidden flex flex-col">
        <ModalHeader title="Edit Profile" subtitle="Update your business details" onClose={() => setShowEditModal(false)} />

        <div className="p-5 sm:p-6 space-y-4 overflow-y-auto">
          <Field label="Business Name *" value={formData.businessName} onChange={(v) => setFormData({ ...formData, businessName: v })} placeholder="e.g. Glow Studio" />
          <Field label="Full Name" value={formData.name} onChange={(v) => setFormData({ ...formData, name: v })} placeholder="Your name" />
          <Field label="Specialization" value={formData.specialization} onChange={(v) => setFormData({ ...formData, specialization: v })} placeholder="e.g. Lash Artist" />
          <Field label="Phone" type="tel" value={formData.phone} onChange={(v) => setFormData({ ...formData, phone: v })} placeholder="+27 ..." />
          <div>
            <label className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em] block mb-1.5">Email (read-only)</label>
            <input
              type="email"
              value={formData.email}
              disabled
              className="w-full bg-gray-50 border border-gray-100 rounded-xl px-4 py-3 text-sm text-gray-500 cursor-not-allowed"
            />
          </div>
          <Field label="Location (short)" value={formData.location} onChange={(v) => setFormData({ ...formData, location: v })} placeholder="e.g. Cape Town" />
          <div>
            <label className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em] block mb-1.5">Bio</label>
            <textarea
              rows="3"
              value={formData.bio}
              onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
              className="w-full bg-[#FCF8F9] border border-transparent rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#CDBCE7] focus:bg-white transition-colors resize-none"
              placeholder="Tell clients about your work..."
            />
          </div>
        </div>

        <ModalFooter>
          <SaveButton onClick={handleSave} saving={savingProfile} label="Save Changes" />
        </ModalFooter>
      </div>
    </div>
  );

  // ============================================================
  // SOCIAL LINKS MODAL
  // ============================================================
  const SocialModal = () => (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50">
      <div className="bg-white rounded-t-3xl sm:rounded-3xl max-w-md w-full max-h-[92vh] overflow-hidden flex flex-col">
        <ModalHeader title="Social Links" subtitle="Let clients connect with you" onClose={() => setShowSocialModal(false)} />

        <div className="p-5 sm:p-6 space-y-4 overflow-y-auto">
          <div className="relative">
            <MessageCircle size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={socialForm.whatsapp}
              onChange={(e) => setSocialForm({ ...socialForm, whatsapp: e.target.value })}
              placeholder="WhatsApp number (e.g. 27821234567)"
              className="w-full bg-[#FCF8F9] border border-transparent rounded-xl pl-11 pr-4 py-3 text-sm focus:outline-none focus:border-[#CDBCE7] focus:bg-white transition-colors"
            />
          </div>

          <div className="relative">
            <Instagram size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={socialForm.instagram}
              onChange={(e) => setSocialForm({ ...socialForm, instagram: e.target.value })}
              placeholder="Instagram username (e.g. @glowstudio)"
              className="w-full bg-[#FCF8F9] border border-transparent rounded-xl pl-11 pr-4 py-3 text-sm focus:outline-none focus:border-[#CDBCE7] focus:bg-white transition-colors"
            />
          </div>

          <div className="relative">
            <Globe size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={socialForm.tiktok}
              onChange={(e) => setSocialForm({ ...socialForm, tiktok: e.target.value })}
              placeholder="TikTok username (e.g. @glowstudio)"
              className="w-full bg-[#FCF8F9] border border-transparent rounded-xl pl-11 pr-4 py-3 text-sm focus:outline-none focus:border-[#CDBCE7] focus:bg-white transition-colors"
            />
          </div>

          <div className="bg-[#FFF5F6] rounded-xl p-3 flex items-start gap-2 mt-4">
            <Info size={14} className="text-[#AF8BD4] shrink-0 mt-0.5" />
            <p className="text-[11px] text-gray-600 leading-relaxed">
              These links will be displayed on your public profile so clients can reach out directly.
            </p>
          </div>
        </div>

        <ModalFooter>
          <SaveButton onClick={handleSaveSocial} saving={savingSocial} label="Save Links" />
        </ModalFooter>
      </div>
    </div>
  );

  // ============================================================
  // BOOKING POLICY MODAL
  // ============================================================
  const BookingPolicyModal = () => (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50">
      <div className="bg-white rounded-t-3xl sm:rounded-3xl max-w-md w-full max-h-[92vh] overflow-hidden flex flex-col">
        <ModalHeader title="Booking Policy" subtitle="Rules for your clients" onClose={() => setShowBookingPolicyModal(false)} />

        <div className="p-5 sm:p-6 space-y-4 overflow-y-auto">
          <div>
            <label className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em] block mb-1.5">
              Your Policy
            </label>
            <textarea
              rows="5"
              value={bookingPolicyForm}
              onChange={(e) => setBookingPolicyForm(e.target.value)}
              placeholder="e.g. A 50% deposit is required to secure all bookings. Cancellations within 24 hours are non-refundable. Please arrive 5 minutes early."
              className="w-full bg-[#FCF8F9] border border-transparent rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#CDBCE7] focus:bg-white transition-colors resize-none"
            />
          </div>

          <div className="bg-[#FFF5F6] rounded-xl p-3 flex items-start gap-2">
            <Info size={14} className="text-[#AF8BD4] shrink-0 mt-0.5" />
            <p className="text-[11px] text-gray-600 leading-relaxed">
              This will be displayed to clients when they select a service, so they know the rules before booking.
            </p>
          </div>
        </div>

        <ModalFooter>
          <SaveButton onClick={handleSaveBookingPolicy} saving={savingBookingPolicy} label="Save Policy" />
        </ModalFooter>
      </div>
    </div>
  );

  // ============================================================
  // PAYMENT POLICY MODAL
  // ============================================================
  const PolicyModal = () => {
    const isDeposit = policyForm.defaultPaymentPolicy === 'deposit';
    return (
      <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50">
        <div className="bg-white rounded-t-3xl sm:rounded-3xl max-w-md w-full max-h-[90vh] overflow-hidden flex flex-col">
          <ModalHeader
            title="Default Payment Policy"
            subtitle="Applied to new services set to 'Use my default'"
            onClose={() => setShowPolicyModal(false)}
          />

          <div className="p-5 sm:p-6 space-y-3 overflow-y-auto">
            {[
              { id: 'after', icon: <Banknote size={16} className="text-[#AF8BD4] shrink-0 mt-0.5" />, title: 'Pay after service', desc: 'Client pays the full amount at their appointment' },
              { id: 'deposit', icon: <CreditCard size={16} className="text-[#AF8BD4] shrink-0 mt-0.5" />, title: 'Deposit required', desc: 'Client pays a deposit to secure, remainder at appointment' },
              { id: 'full', icon: <Wallet size={16} className="text-[#AF8BD4] shrink-0 mt-0.5" />, title: 'Full payment upfront', desc: 'Client pays the full amount before the appointment' },
            ].map(opt => (
              <button
                key={opt.id}
                type="button"
                onClick={() => setPolicyForm({ ...policyForm, defaultPaymentPolicy: opt.id })}
                className={`w-full text-left p-3 rounded-xl border transition-colors flex items-start gap-3 ${
                  policyForm.defaultPaymentPolicy === opt.id
                    ? 'border-[#AF8BD4] bg-[#F4EEFF]'
                    : 'border-gray-100 hover:border-[#CDBCE7]'
                }`}
              >
                {opt.icon}
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm text-gray-900">{opt.title}</p>
                  <p className="text-[11px] text-gray-500 mt-0.5">{opt.desc}</p>
                </div>
              </button>
            ))}

            {isDeposit && (
              <div className="bg-[#FCF8F9] rounded-xl p-4 mt-2">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] font-bold text-gray-700">Default deposit percentage</span>
                  <span className="text-[11px] font-bold text-[#AF8BD4]">
                    {policyForm.defaultDepositPercent}%
                  </span>
                </div>
                <input
                  type="range"
                  min="10"
                  max="100"
                  step="5"
                  value={policyForm.defaultDepositPercent}
                  onChange={(e) => setPolicyForm({ ...policyForm, defaultDepositPercent: parseInt(e.target.value) })}
                  className="w-full accent-[#AF8BD4]"
                />
              </div>
            )}
          </div>

          <ModalFooter>
            <SaveButton onClick={handleSavePolicy} saving={savingPolicy} label="Save Policy" />
          </ModalFooter>
        </div>
      </div>
    );
  };

  // ============================================================
  // PAYMENT METHODS MODAL
  // ============================================================
  const PaymentMethodsModal = () => (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50">
      <div className="bg-white rounded-t-3xl sm:rounded-3xl max-w-md w-full max-h-[92vh] overflow-hidden flex flex-col">
        <ModalHeader
          title="Payment Methods"
          subtitle="How clients pay you for services"
          onClose={() => setShowPaymentModal(false)}
        />

        <div className="p-5 sm:p-6 space-y-5 overflow-y-auto">
          <div className="bg-[#FCF8F9] rounded-2xl p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Smartphone size={18} className="text-[#AF8BD4]" />
                <span className="font-bold text-sm text-gray-900">PayShap</span>
              </div>
              <ToggleSwitch
                value={paymentForm.acceptPayShap}
                onChange={(v) => setPaymentForm({ ...paymentForm, acceptPayShap: v })}
              />
            </div>

            {paymentForm.acceptPayShap && (
              <div className="space-y-3 mt-3">
                <Field
                  label="PayShap ID *"
                  value={paymentForm.payshapId}
                  onChange={(v) => setPaymentForm({ ...paymentForm, payshapId: v })}
                  placeholder="e.g. 0821234567@standardbank"
                />
                <Field
                  label="Display Name"
                  value={paymentForm.payshapName}
                  onChange={(v) => setPaymentForm({ ...paymentForm, payshapName: v })}
                  placeholder="e.g. Glow Studio"
                />
                <div>
                  <label className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em] block mb-1.5">
                    Note for clients
                  </label>
                  <textarea
                    rows="2"
                    value={paymentForm.payshapNote}
                    onChange={(e) => setPaymentForm({ ...paymentForm, payshapNote: e.target.value })}
                    className="w-full bg-white border border-gray-100 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#CDBCE7] transition-colors resize-none"
                    placeholder="e.g. Use your first name as reference"
                  />
                </div>
              </div>
            )}
          </div>

          <div className="bg-[#FCF8F9] rounded-2xl p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Banknote size={18} className="text-[#AF8BD4]" />
                <span className="font-bold text-sm text-gray-900">Cash at appointment</span>
              </div>
              <ToggleSwitch
                value={paymentForm.acceptCash}
                onChange={(v) => setPaymentForm({ ...paymentForm, acceptCash: v })}
              />
            </div>

            {paymentForm.acceptCash && (
              <div className="mt-3">
                <label className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em] block mb-1.5">
                  Note for clients
                </label>
                <textarea
                  rows="2"
                  value={paymentForm.cashNote}
                  onChange={(e) => setPaymentForm({ ...paymentForm, cashNote: e.target.value })}
                  className="w-full bg-white border border-gray-100 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#CDBCE7] transition-colors resize-none"
                  placeholder="e.g. Cash only, no card machine on site"
                />
              </div>
            )}
          </div>

          <div className="bg-[#FFF5F6] rounded-xl p-3 flex items-start gap-2">
            <Info size={14} className="text-[#AF8BD4] shrink-0 mt-0.5" />
            <p className="text-[11px] text-gray-600 leading-relaxed">
              Clients will see these details when they pay for a booking. You'll mark each payment received once you've checked your bank or wallet.
            </p>
          </div>
        </div>

        <ModalFooter>
          <SaveButton onClick={handleSavePayment} saving={savingPayment} label="Save Payment Methods" />
        </ModalFooter>
      </div>
    </div>
  );

  // ============================================================
  // BANKING MODAL
  // ============================================================
  const BankModal = () => (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50">
      <div className="bg-white rounded-t-3xl sm:rounded-3xl max-w-md w-full max-h-[92vh] overflow-hidden flex flex-col">
        <ModalHeader
          title="Banking Details"
          subtitle="Where Skytone sends your payouts"
          onClose={() => setShowBankModal(false)}
        />

        <div className="p-5 sm:p-6 space-y-4 overflow-y-auto">
          <div>
            <label className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em] block mb-1.5">Bank *</label>
            <select
              value={bankForm.bankName}
              onChange={(e) => setBankForm({ ...bankForm, bankName: e.target.value })}
              className="w-full bg-[#FCF8F9] border border-transparent rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#CDBCE7] focus:bg-white transition-colors cursor-pointer"
            >
              <option value="">Select your bank</option>
              {SA_BANKS.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>

          <Field
            label="Account Holder *"
            value={bankForm.accountHolder}
            onChange={(v) => setBankForm({ ...bankForm, accountHolder: v })}
            placeholder="Name as it appears on the account"
          />

          <Field
            label="Account Number *"
            value={bankForm.accountNumber}
            onChange={(v) => setBankForm({ ...bankForm, accountNumber: v.replace(/\D/g, '') })}
            placeholder="e.g. 1234567890"
          />

          <Field
            label="Branch Code"
            value={bankForm.branchCode}
            onChange={(v) => setBankForm({ ...bankForm, branchCode: v.replace(/\D/g, '') })}
            placeholder="e.g. 051001"
          />

          <div>
            <label className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em] block mb-1.5">Account Type</label>
            <div className="grid grid-cols-3 gap-2">
              {['cheque', 'savings', 'transmission'].map(t => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setBankForm({ ...bankForm, accountType: t })}
                  className={`py-2.5 rounded-xl text-xs font-bold capitalize transition-colors border ${
                    bankForm.accountType === t
                      ? 'bg-[#010101] text-white border-[#010101]'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-[#CDBCE7]'
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-[#FFF5F6] rounded-xl p-3 flex items-start gap-2">
            <Info size={14} className="text-[#AF8BD4] shrink-0 mt-0.5" />
            <p className="text-[11px] text-gray-600 leading-relaxed">
              Your banking details are stored securely and only used for paying out your earnings from completed bookings and orders.
            </p>
          </div>
        </div>

        <ModalFooter>
          <SaveButton onClick={handleSaveBank} saving={savingBank} label="Save Banking Details" />
        </ModalFooter>
      </div>
    </div>
  );

  // ============================================================
  // WORKING HOURS MODAL
  // ============================================================
  const HoursModal = () => (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50">
      <div className="bg-white rounded-t-3xl sm:rounded-3xl max-w-md w-full max-h-[92vh] overflow-hidden flex flex-col">
        <ModalHeader
          title="Working Hours"
          subtitle="When clients can book you"
          onClose={() => setShowHoursModal(false)}
        />

        <div className="p-5 sm:p-6 space-y-3 overflow-y-auto">
          {DAYS_OF_WEEK.map(day => {
            const d = hoursForm[day.id] || { enabled: false, open: '09:00', close: '17:00' };
            return (
              <div key={day.id} className="bg-[#FCF8F9] rounded-2xl p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-bold text-sm text-gray-900">{day.label}</span>
                  <ToggleSwitch
                    value={d.enabled}
                    onChange={(v) => setHoursForm({
                      ...hoursForm,
                      [day.id]: { ...d, enabled: v },
                    })}
                  />
                </div>

                {d.enabled && (
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider block mb-1">Open</label>
                      <input
                        type="time"
                        value={d.open}
                        onChange={(e) => setHoursForm({
                          ...hoursForm,
                          [day.id]: { ...d, open: e.target.value },
                        })}
                        className="w-full bg-white border border-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#CDBCE7]"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider block mb-1">Close</label>
                      <input
                        type="time"
                        value={d.close}
                        onChange={(e) => setHoursForm({
                          ...hoursForm,
                          [day.id]: { ...d, close: e.target.value },
                        })}
                        className="w-full bg-white border border-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#CDBCE7]"
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <ModalFooter>
          <SaveButton onClick={handleSaveHours} saving={savingHours} label="Save Working Hours" />
        </ModalFooter>
      </div>
    </div>
  );

  // ============================================================
  // LOCATION MODAL
  // ============================================================
  const LocationModal = () => {
    return (
      <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50">
        <div className="bg-white rounded-t-3xl sm:rounded-3xl max-w-md w-full max-h-[92vh] overflow-hidden flex flex-col">
          <ModalHeader
            title="Location"
            subtitle="Where your business is based"
            onClose={() => setShowLocationModal(false)}
          />

          <div className="p-5 sm:p-6 space-y-4 overflow-y-auto">
            <div className="relative">
              <label className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em] block mb-1.5">
                Search Address *
              </label>
              <div className="relative">
                <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={addressQuery}
                  onChange={(e) => {
                    setAddressQuery(e.target.value);
                    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
                    
                    if (e.target.value.length >= 3) {
                      setSearching(true);
                      setShowSuggestions(true);
                      searchTimerRef.current = setTimeout(async () => {
                        const results = await searchAddress(e.target.value);
                        setSuggestions(results);
                        setSearching(false);
                      }, 600);
                    } else {
                      setSuggestions([]);
                      setShowSuggestions(false);
                      setSearching(false);
                    }
                  }}
                  onFocus={() => {
                    if (suggestions.length > 0) setShowSuggestions(true);
                  }}
                  placeholder="e.g. 42 Long Street, Cape Town"
                  className="w-full bg-[#FCF8F9] border border-transparent rounded-xl pl-11 pr-10 py-3 text-sm focus:outline-none focus:border-[#CDBCE7] focus:bg-white transition-colors"
                />
                {searching && (
                  <div className="absolute right-4 top-1/2 -translate-y-1/2">
                    <div className="w-4 h-4 border-2 border-[#AF8BD4] border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}
              </div>

              {showSuggestions && suggestions.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-100 rounded-xl shadow-lg z-50 max-h-48 overflow-y-auto">
                  {suggestions.map((place) => (
                    <button
                      key={place.place_id}
                      type="button"
                      onClick={() => {
                        const parts = place.display_name.split(', ');
                        const city = parts.length > 1 ? parts[parts.length - 2] : parts[0];
                        const line1 = parts.slice(0, -2).join(', ') || place.display_name;
                        
                        setAddressQuery(place.display_name);
                        setLocationForm(prev => ({
                          ...prev,
                          line1: line1,
                          city: city,
                          lat: parseFloat(place.lat),
                          lng: parseFloat(place.lon),
                        }));
                        setShowSuggestions(false);
                        showToast('Address selected');
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-[#FCF8F9] border-b border-gray-50 last:border-0 text-sm text-gray-700"
                    >
                      {place.display_name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Field
                label="Street Address *"
                value={locationForm.line1}
                onChange={(v) => setLocationForm({ ...locationForm, line1: v })}
                placeholder="e.g. 42 Long Street"
              />
              <Field
                label="City *"
                value={locationForm.city}
                onChange={(v) => setLocationForm({ ...locationForm, city: v })}
                placeholder="e.g. Cape Town"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Field
                label="Postal Code"
                value={locationForm.postalCode}
                onChange={(v) => setLocationForm({ ...locationForm, postalCode: v })}
                placeholder="8001"
              />
              <Field
                label="Country"
                value={locationForm.country}
                onChange={(v) => setLocationForm({ ...locationForm, country: v })}
                placeholder="South Africa"
              />
            </div>

            <div className="bg-[#FCF8F9] rounded-2xl p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Locate size={16} className="text-[#AF8BD4]" />
                  <span className="font-bold text-sm text-gray-900">Pin on map</span>
                </div>
                <button
                  type="button"
                  onClick={handleGeolocate}
                  disabled={locating}
                  className="text-[11px] font-bold text-[#AF8BD4] hover:underline disabled:opacity-50 flex items-center gap-1"
                >
                  {locating ? (
                    <>
                      <div className="w-3 h-3 border-2 border-[#AF8BD4] border-t-transparent rounded-full animate-spin"></div>
                      Locating...
                    </>
                  ) : (
                    'Use current location'
                  )}
                </button>
              </div>
              {locationForm.lat && locationForm.lng ? (
                <p className="text-[11px] text-[#AF8BD4] font-bold bg-[#F4EEFF] p-2 rounded-lg">
                  📍 Exact location pinned: {locationForm.lat.toFixed(5)}, {locationForm.lng.toFixed(5)}
                </p>
              ) : (
                <p className="text-[11px] text-gray-400">
                  Use the search bar above or click "Use current location" to get pinned exactly on the map.
                </p>
              )}
            </div>

            <div className="bg-[#FFF5F6] rounded-xl p-3 flex items-start gap-2">
              <Info size={14} className="text-[#AF8BD4] shrink-0 mt-0.5" />
              <p className="text-[11px] text-gray-600 leading-relaxed">
                Your address is shared with clients only when they book you. Adding coordinates helps with route planning and location-based discovery.
              </p>
            </div>
          </div>

          <ModalFooter>
            <SaveButton onClick={handleSaveLocation} saving={savingLocation} label="Save Location" />
          </ModalFooter>
        </div>
      </div>
    );
  };

  // ============================================================
  // MAP VISIBILITY MODAL
  // ============================================================
  const MapVisibilityModal = () => {
    const hasCoords = !!userData?.coordinates?.lat;
    return (
      <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50">
        <div className="bg-white rounded-t-3xl sm:rounded-3xl max-w-md w-full max-h-[90vh] overflow-hidden flex flex-col">
          <ModalHeader
            title="Show me on the map"
            subtitle="Control your map visibility"
            onClose={() => setShowMapVisibilityModal(false)}
          />

          <div className="p-5 sm:p-6 space-y-4 overflow-y-auto">
            {!hasCoords && (
              <div className="bg-[#FFF5F6] rounded-xl p-3 flex items-start gap-2">
                <Info size={14} className="text-[#AF8BD4] shrink-0 mt-0.5" />
                <p className="text-[11px] text-gray-600 leading-relaxed">
                  You haven't pinned your studio yet. Open <span className="font-bold">Location</span> first to add your address and pin.
                </p>
              </div>
            )}

            <div className="bg-[#FCF8F9] rounded-2xl p-4">
              <div className="flex items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    {showOnMap ? <Eye size={16} className="text-[#AF8BD4]" /> : <EyeOff size={16} className="text-gray-400" />}
                    <span className="font-bold text-sm text-gray-900">
                      {showOnMap ? 'Visible on the map' : 'Hidden from the map'}
                    </span>
                  </div>
                  <p className="text-[11px] text-gray-500 leading-relaxed">
                    {showOnMap
                      ? 'Clients can find your studio when browsing the map. You\'ll also show in distance sorting everywhere.'
                      : 'Your pin is hidden from the map. You still appear in search results and distance sorting.'}
                  </p>
                </div>
                <ToggleSwitch
                  value={showOnMap}
                  onChange={(v) => setShowOnMap(v)}
                />
              </div>
            </div>

            <div className="bg-[#FFF5F6] rounded-xl p-3 flex items-start gap-2">
              <Info size={14} className="text-[#AF8BD4] shrink-0 mt-0.5" />
              <p className="text-[11px] text-gray-600 leading-relaxed">
                You can change this at any time. Turning the map pin off does <span className="font-bold">not</span> delete your address or coordinates.
              </p>
            </div>
          </div>

          <ModalFooter>
            <SaveButton onClick={handleSaveMapVisibility} saving={savingMapVisibility} label="Save Preference" />
          </ModalFooter>
        </div>
      </div>
    );
  };

  // ============================================================
  // PRIVACY & SECURITY MODAL
  // ============================================================
  const PrivacyModal = () => (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50">
      <div className="bg-white rounded-t-3xl sm:rounded-3xl max-w-md w-full max-h-[92vh] overflow-hidden flex flex-col">
        <ModalHeader
          title="Privacy & Security"
          subtitle="Keep your account safe"
          onClose={() => setShowPrivacyModal(false)}
        />

        <div className="p-5 sm:p-6 space-y-3 overflow-y-auto">
          <div className="bg-[#FCF8F9] rounded-2xl p-4 flex items-start gap-3">
            <Key size={18} className="text-[#AF8BD4] shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-bold text-sm text-gray-900">Password</p>
              <p className="text-[11px] text-gray-500 mt-0.5">
                Managed by Firebase Auth. Use “Forgot password” on the login screen to reset it.
              </p>
            </div>
          </div>

          <div className="bg-[#FCF8F9] rounded-2xl p-4 flex items-start gap-3">
            <ShieldCheck size={18} className="text-[#AF8BD4] shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-bold text-sm text-gray-900">Two-factor authentication</p>
              <p className="text-[11px] text-gray-500 mt-0.5">
                Coming soon — you'll be able to add an SMS or authenticator app as a second step.
              </p>
            </div>
          </div>

          <div className="bg-[#FCF8F9] rounded-2xl p-4 flex items-start gap-3">
            <Download size={18} className="text-[#AF8BD4] shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-bold text-sm text-gray-900">Download your data</p>
              <p className="text-[11px] text-gray-500 mt-0.5">
                Request an export of your profile, services, and bookings.
              </p>
              <button
                type="button"
                onClick={() => showToast('Data export requested — we\'ll email you shortly')}
                className="mt-2 text-[11px] font-bold text-[#AF8BD4] hover:underline"
              >
                Request export
              </button>
            </div>
          </div>

          <div className="bg-[#FFF5F6] rounded-xl p-3 flex items-start gap-2">
            <Info size={14} className="text-[#AF8BD4] shrink-0 mt-0.5" />
            <p className="text-[11px] text-gray-600 leading-relaxed">
              We never share your banking details or address with clients until you accept a booking.
            </p>
          </div>
        </div>

        <ModalFooter>
          <button
            onClick={() => { setShowPrivacyModal(false); handleLogout(); }}
            className="w-full bg-[#010101] text-white py-4 rounded-full font-bold text-sm hover:bg-black/90 transition-colors"
          >
            Sign Out of All Devices
          </button>
        </ModalFooter>
      </div>
    </div>
  );

  // ============================================================
  // NOTIFICATIONS MODAL
  // ============================================================
  const NotificationsModal = () => (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50">
      <div className="bg-white rounded-t-3xl sm:rounded-3xl max-w-md w-full max-h-[92vh] overflow-hidden flex flex-col">
        <ModalHeader
          title="Notifications"
          subtitle="Choose what you want to hear about"
          onClose={() => setShowNotificationsModal(false)}
        />

        <div className="p-5 sm:p-6 space-y-3 overflow-y-auto">
          {[
            { key: 'bookingUpdates', icon: <Calendar size={18} className="text-[#AF8BD4]" />, title: 'Booking updates', desc: 'New bookings, cancellations, reschedules' },
            { key: 'messages', icon: <Mail size={18} className="text-[#AF8BD4]" />, title: 'Messages', desc: 'When a client sends you a message' },
            { key: 'payments', icon: <DollarSign size={18} className="text-[#AF8BD4]" />, title: 'Payments', desc: 'When a payment is received or a payout is sent' },
            { key: 'marketing', icon: <Star size={18} className="text-[#AF8BD4]" />, title: 'Tips & offers', desc: 'Product updates and growth tips (optional)' },
            { key: 'quietHours', icon: <Clock size={18} className="text-[#AF8BD4]" />, title: 'Quiet hours', desc: 'Mute push notifications between 20:00 and 07:00' },
          ].map(item => (
            <div key={item.key} className="bg-[#FCF8F9] rounded-2xl p-4 flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center shrink-0">
                {item.icon}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm text-gray-900">{item.title}</p>
                <p className="text-[11px] text-gray-500 mt-0.5">{item.desc}</p>
              </div>
              <ToggleSwitch
                value={notificationPrefs[item.key]}
                onChange={(v) => setNotificationPrefs(prev => ({ ...prev, [item.key]: v }))}
              />
            </div>
          ))}
        </div>

        <ModalFooter>
          <SaveButton onClick={handleSaveNotifications} saving={savingNotifications} label="Save Preferences" />
        </ModalFooter>
      </div>
    </div>
  );

  // ============================================================
  // DELETE ACCOUNT MODAL
  // ============================================================
  const DeleteAccountModal = () => (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50">
      <div className="bg-white rounded-t-3xl sm:rounded-3xl max-w-md w-full overflow-hidden">
        <div className="p-6">
          <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle size={26} className="text-red-500" />
          </div>
          <h2 className="font-serif font-bold text-xl text-gray-900 text-center mb-2">
            Delete your account?
          </h2>
          <p className="text-sm text-gray-500 text-center mb-6">
            This will permanently remove your profile, services and products. Bookings already made will remain in clients' history. This cannot be undone.
          </p>

          <label className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em] block mb-1.5">
            Type <span className="text-red-500">DELETE</span> to confirm
          </label>
          <input
            type="text"
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            placeholder="DELETE"
            className="w-full bg-[#FCF8F9] border border-transparent rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-red-400 focus:bg-white transition-colors"
          />

          <div className="flex gap-3 mt-5">
            <button
              onClick={() => {
                setShowDeleteModal(false);
                setDeleteConfirmText('');
              }}
              disabled={deletingAccount}
              className="flex-1 py-3.5 rounded-full bg-[#FCF8F9] text-gray-700 font-bold text-sm hover:bg-gray-100 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteAccount}
              disabled={deletingAccount || deleteConfirmText.trim().toUpperCase() !== 'DELETE'}
              className="flex-1 py-3.5 rounded-full bg-red-500 text-white font-bold text-sm hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {deletingAccount ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // ============================================================
  // SWITCH TO CLIENT MODAL
  // ============================================================
  const SwitchModal = () => (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50">
      <div className="bg-white rounded-t-3xl sm:rounded-3xl max-w-md w-full overflow-hidden">
        <div className="p-6 text-center">
          <div className="w-16 h-16 bg-[#F4EEFF] rounded-full flex items-center justify-center mx-auto mb-4">
            <Repeat size={26} className="text-[#AF8BD4]" />
          </div>
          <h2 className="font-serif font-bold text-xl text-gray-900 mb-2">
            Switch to Client Mode?
          </h2>
          <p className="text-sm text-gray-500 mb-6">
            You'll be signed out and returned to the login screen. Log back in with the same account to continue as a client.
          </p>

          <div className="flex gap-3">
            <button
              onClick={() => setShowSwitchModal(false)}
              disabled={switchingMode}
              className="flex-1 py-3.5 rounded-full bg-[#FCF8F9] text-gray-700 font-bold text-sm hover:bg-gray-100 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={confirmSwitchToClient}
              disabled={switchingMode}
              className="flex-1 py-3.5 rounded-full bg-[#010101] text-white font-bold text-sm hover:bg-black/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {switchingMode ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Switching...
                </>
              ) : (
                'Continue'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // ============================================================
  // ROOT
  // ============================================================
  return (
    <>
      <DesktopView />
      <MobileView />
      {showEditModal && <EditModal />}
      {showSocialModal && <SocialModal />}
      {showBookingPolicyModal && <BookingPolicyModal />}
      {showPolicyModal && <PolicyModal />}
      {showPaymentModal && <PaymentMethodsModal />}
      {showBankModal && <BankModal />}
      {showHoursModal && <HoursModal />}
      {showLocationModal && <LocationModal />}
      {showMapVisibilityModal && <MapVisibilityModal />}
      {showPrivacyModal && <PrivacyModal />}
      {showNotificationsModal && <NotificationsModal />}
      {showDeleteModal && <DeleteAccountModal />}
      {showSwitchModal && <SwitchModal />}

      {toast && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[110]">
          <div className={`px-5 py-3 rounded-full shadow-lg text-sm font-bold ${
            toast.type === 'error' ? 'bg-red-500 text-white' : 'bg-[#010101] text-white'
          }`}>
            {toast.msg}
          </div>
        </div>
      )}
    </>
  );
};

// ============================================================
// Small reusable components
// ============================================================
const ModalHeader = ({ title, subtitle, onClose }) => (
  <div className="p-5 sm:p-6 border-b border-gray-50 flex items-center justify-between shrink-0">
    <div className="min-w-0 flex-1">
      <h2 className="font-serif font-bold text-xl text-gray-900 truncate">{title}</h2>
      {subtitle && <p className="text-xs text-gray-400 mt-0.5 truncate">{subtitle}</p>}
    </div>
    <button
      onClick={onClose}
      className="w-9 h-9 rounded-full bg-[#FCF8F9] flex items-center justify-center hover:bg-gray-100 shrink-0 ml-2"
    >
      <X size={18} />
    </button>
  </div>
);

const ModalFooter = ({ children }) => (
  <div className="p-5 sm:p-6 pt-4 border-t border-gray-50 shrink-0">
    {children}
  </div>
);

const SaveButton = ({ onClick, saving, label }) => (
  <button
    onClick={onClick}
    disabled={saving}
    className="w-full bg-[#010101] text-white py-4 rounded-full font-bold text-sm hover:bg-black/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
  >
    {saving ? (
      <>
        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
        Saving...
      </>
    ) : (
      <><Check size={16} /> {label}</>
    )}
  </button>
);

const Field = ({ label, type = 'text', value, onChange, placeholder }) => (
  <div>
    <label className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em] block mb-1.5">
      {label}
    </label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full bg-[#FCF8F9] border border-transparent rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#CDBCE7] focus:bg-white transition-colors"
    />
  </div>
);

const ToggleSwitch = ({ value, onChange }) => (
  <button
    type="button"
    onClick={() => onChange(!value)}
    className={`w-11 h-6 rounded-full transition-colors relative flex items-center p-0.5 shrink-0 ${
      value ? 'bg-[#AF8BD4]' : 'bg-gray-200'
    }`}
  >
    <div className={`w-5 h-5 bg-white rounded-full shadow-sm transition-transform ${
      value ? 'translate-x-5' : 'translate-x-0'
    }`}></div>
  </button>
);

export default ProfessionalProfilePage;